import React, { useState } from 'react';
import {
  X,
  Send,
  ShieldCheck,
  DollarSign,
  Clock,
  Calendar,
  Lock,
  AlertCircle,
  CreditCard,
  ArrowRight,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { Job } from '../../types';
import { useApp } from '../../context/AppContext';
import { getAssignedProfessionalId, isJobForProfessionalTrade } from '../../lib/matchingEngine';

interface SubmitQuoteModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
}

export const SubmitQuoteModal: React.FC<SubmitQuoteModalProps> = ({ job, isOpen, onClose }) => {
  const { currentUser, submitQuote, quotes } = useApp();

  const existingQuote = currentUser ? quotes.find((q) => q.jobId === job.id && q.professionalId === currentUser.id) : undefined;

  const defaultLabor = job.budgetEstimate && job.budgetEstimate > 0 ? Math.round(job.budgetEstimate * 0.6) : 18000;
  const defaultMaterials = job.budgetEstimate && job.budgetEstimate > 0 ? Math.round(job.budgetEstimate * 0.4) : 15000;
  const [laborCost, setLaborCost] = useState<number>(defaultLabor);
  const [materialsCost, setMaterialsCost] = useState<number>(defaultMaterials);
  const [durationEstimate, setDurationEstimate] = useState('1 Day (approx. 4-6 hours)');
  const [startDate, setStartDate] = useState('Tomorrow morning 9:00 AM');
  const [warrantyDays, setWarrantyDays] = useState<number>(30);
  const [notes, setNotes] = useState(
    'I have reviewed your repair description and photos. I will arrive with standard diagnostic tools and genuine replacement parts. 30-day workmanship guarantee included.'
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen || !currentUser) return null;

  const isVerified =
    currentUser.verificationStatus === 'verified' || (currentUser as any).isVerified === true;

  const totalAmount = (Number(laborCost) || 0) + (Number(materialsCost) || 0);
  const estimatedCommission = Math.round((totalAmount * 10) / 100);
  const estimatedTakeHome = totalAmount - estimatedCommission;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setErrorMsg(null);
    setSuccessMessage(null);

    const assignedProId = getAssignedProfessionalId(job);
    if (assignedProId && assignedProId !== currentUser.id) {
      setErrorMsg('This job has already been assigned to another professional.');
      return;
    }

    if (!isJobForProfessionalTrade(job, currentUser)) {
      setErrorMsg(
        `Trade Mismatch: This job requires a verified ${job.categoryName || job.categorySlug}, which does not match your registered trade (${currentUser.primaryCategory}).`
      );
      return;
    }

    if (existingQuote) {
      setErrorMsg('Quote has already been sent for this job.');
      return;
    }

    if (totalAmount <= 0) {
      setErrorMsg('Please enter a valid labor cost.');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitQuote({
        jobId: job.id,
        professionalId: currentUser.id,
        professionalName: currentUser.fullName,
        professionalAvatar: currentUser.avatarUrl,
        professionalBusiness: currentUser.businessName || `${currentUser.fullName} Services`,
        professionalCategory: currentUser.primaryCategory || job.categorySlug,
        professionalRating: currentUser.rating || 5.0,
        professionalReviewCount: currentUser.reviewCount || 0,
        professionalVerification: currentUser.verificationStatus || 'verified',
        professionalYearsExp: currentUser.yearsExperience || 5,
        totalAmount,
        laborCost: Number(laborCost),
        materialsCost: Number(materialsCost),
        durationEstimate,
        startDate,
        estimatedArrivalTime: startDate,
        warrantyDays: Number(warrantyDays),
        notes,
      });

      setSuccessMessage('Quote submitted successfully! The customer has been notified and can now review your proposal.');
      setTimeout(() => {
        onClose();
      }, 1400);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to submit quote.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-lg w-full p-4 sm:p-8 border border-slate-200 shadow-2xl relative max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-4 sm:mb-6 pr-8">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Professional Quote Submission</span>
          </div>
          <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Submit Your Quote</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            For: <strong className="text-slate-800">{job.title}</strong> ({job.lga}, {job.state})
          </p>

          {job.budgetEstimate && job.budgetEstimate > 0 ? (
            <div className="mt-2.5 p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs">
              <span className="text-slate-600 font-medium">Customer's Stated Target Budget:</span>
              <span className="font-black text-emerald-800 text-sm">₦{Number(job.budgetEstimate).toLocaleString()}</span>
            </div>
          ) : (
            <div className="mt-2.5 p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-[11px]">
              Customer indicated budget is open for professional pricing.
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 text-xs">
          {existingQuote && (
            <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-950 text-xs flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 font-bold">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Quote Has Already Been Sent (₦{existingQuote.totalAmount.toLocaleString()})</span>
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {!isVerified && (
            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2.5 leading-relaxed">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-amber-950">Identity & Safety Verification Required</p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Your account status is <strong>{currentUser?.verificationStatus || 'unverified'}</strong>. Only administrator-verified professionals can submit quotes to customers. Please complete verification in your dashboard to start quoting on jobs.
                </p>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span className="font-semibold">{successMessage}</span>
            </div>
          )}

          {/* Price Breakdown in Naira */}
          <div className="p-3.5 sm:p-4 rounded-2xl bg-emerald-50/60 border border-emerald-100 space-y-3">
            <span className="font-bold text-xs text-emerald-950 block">Pricing Breakdown (₦)</span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Professional Labor (₦)</label>
                <input
                  type="number"
                  step="500"
                  required
                  value={laborCost}
                  onChange={(e) => setLaborCost(Number(e.target.value))}
                  className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500 bg-white min-h-[44px] sm:min-h-0"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Materials / Parts (₦)</label>
                <input
                  type="number"
                  step="500"
                  value={materialsCost}
                  onChange={(e) => setMaterialsCost(Number(e.target.value))}
                  className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500 bg-white min-h-[44px] sm:min-h-0"
                />
              </div>
            </div>

            <div className="border-t border-emerald-200/80 pt-2.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-600">Total Customer Price:</span>
                <span className="text-base sm:text-lg font-extrabold text-slate-900">
                  ₦{totalAmount.toLocaleString()}
                </span>
              </div>
              {job.budgetEstimate && job.budgetEstimate > 0 && (
                <div className="flex items-center justify-between text-[11px] pt-0.5">
                  <span className="text-slate-500">Vs Customer Budget:</span>
                  <span className={`font-bold ${totalAmount <= job.budgetEstimate ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {totalAmount <= job.budgetEstimate
                      ? `✓ ₦${(job.budgetEstimate - totalAmount).toLocaleString()} within budget`
                      : `⚠ ₦${(totalAmount - job.budgetEstimate).toLocaleString()} above customer target`}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>JobMatch Marketplace Fee (10% on payment):</span>
                <span className="font-medium text-slate-700">-₦{estimatedCommission.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs pt-1 border-t border-emerald-200/60">
                <span className="font-bold text-emerald-900">Your Estimated Take-Home:</span>
                <span className="font-extrabold text-emerald-700 text-sm">
                  ₦{estimatedTakeHome.toLocaleString()} (90%)
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            <div>
              <label className="block font-semibold text-slate-700 mb-1">Estimated Duration</label>
              <input
                type="text"
                required
                placeholder="e.g. Same Day (4 hours)"
                value={durationEstimate}
                onChange={(e) => setDurationEstimate(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500 min-h-[44px] sm:min-h-0"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 mb-1">Estimated Arrival Time</label>
              <input
                type="text"
                required
                placeholder="e.g. Within 2 hours / Today 2:00 PM"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500 min-h-[44px] sm:min-h-0"
              />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {['Within 1 hour', 'Within 2 hours', 'Today 3:00 PM', 'Tomorrow 9:00 AM'].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setStartDate(preset)}
                    className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-colors cursor-pointer ${
                      startDate === preset
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">Workmanship Guarantee (Days)</label>
            <select
              value={warrantyDays}
              onChange={(e) => setWarrantyDays(Number(e.target.value))}
              className="w-full px-3 py-2.5 sm:py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500 bg-white min-h-[44px] sm:min-h-0"
            >
              <option value={14}>14 Days Workmanship Warranty</option>
              <option value={30}>30 Days Workmanship Warranty (Standard)</option>
              <option value={60}>60 Days Workmanship Warranty</option>
              <option value={90}>90 Days Extended Workmanship Warranty</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 mb-1">Message / Proposal Note to Customer</label>
            <textarea
              rows={3}
              required
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Explain your approach, why you are qualified, what tools you bring..."
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-base sm:text-sm focus:outline-hidden focus:border-emerald-500"
            />
          </div>

          <div className="pt-3 border-t border-slate-200 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-bold text-xs hover:bg-slate-50 min-h-[44px] cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              id="submit-quote-btn"
              disabled={isSubmitting || Boolean(successMessage) || !isVerified || Boolean(existingQuote)}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md flex items-center justify-center gap-1.5 min-h-[44px] cursor-pointer disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Submitting Quote...</span>
                </>
              ) : existingQuote ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                  <span>Quote Already Sent</span>
                </>
              ) : successMessage ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Quote Sent!</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Send Quote to Customer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
